import {useEnvConfig} from '@web-catalog/env';
import {useCallback, useEffect, useRef, useState} from 'react';

import {
  AdResponseType,
  useAdAnalytics,
} from '@web-catalog/components/Ads/useAdAnalytics';

import {safeSessionStorage} from '@web-catalog/utils/safeStorages';

import {useWindowFocusListener} from './useWindowFocusListener';
import {
  AD_AUTOREFRESH_INTERVAL_MS,
  bindClickLogger,
  getAdvManager,
  log,
} from './utils';
import YandexAdScript from './YandexAdScript';

type Props = {
  mode: 'floor_ad' | 'floor_ad_closed' | 'floor_ad_closed_copy';
};

let adsHeight: number;

const forceFooterBottomPadding = (height: number | undefined) => {
  const footer = document.querySelector('footer');

  if (height && footer) {
    adsHeight = height;
    const currentPB = window.getComputedStyle(footer).paddingBottom;
    footer.style.paddingBottom = `calc(${currentPB} + ${adsHeight}px)`;
  }
};

const removeFooterBottomPadding = () => {
  const footer = document.querySelector('footer');

  if (footer) {
    const currentPB = window.getComputedStyle(footer).paddingBottom;
    footer.style.paddingBottom = `calc(${currentPB} - ${adsHeight}px)`;
  }
};

const sessionStorageKey = 'YandexFloorAdClosed';

export const YandexFloorAd = ({mode}: Props) => {
  const {
    YANDEX_ADS_FLOOR_AD_DESKTOP_ID,
    YANDEX_ADS_FLOOR_AD_MOBILE_ID,
    YANDEX_ADS_FLOOR_AD_ALT_DESKTOP_ID,
    YANDEX_ADS_FLOOR_AD_ALT_MOBILE_ID,
  } = useEnvConfig();

  const desktopBlockId =
    mode == 'floor_ad_closed_copy'
      ? YANDEX_ADS_FLOOR_AD_ALT_DESKTOP_ID
      : YANDEX_ADS_FLOOR_AD_DESKTOP_ID;

  const mobileBlockId =
    mode === 'floor_ad_closed_copy'
      ? YANDEX_ADS_FLOOR_AD_ALT_MOBILE_ID
      : YANDEX_ADS_FLOOR_AD_MOBILE_ID;

  const [adWasAlreadyClosedInSession] = useState(() =>
    safeSessionStorage.getItem(sessionStorageKey),
  );
  const [adWasClosed, setAdWasClosed] = useState(false);

  const canShowAd =
    !adWasClosed && (mode === 'floor_ad' || !adWasAlreadyClosedInSession);

  const adLog = useAdAnalytics({platform: 'yandex', adType: mode});
  const renderWasCalledRef = useRef(false);
  const lastInitTs = useRef(0);

  const onScriptError = useCallback(() => {
    adLog.blocked(
      window.matchMedia('(pointer: coarse)').matches
        ? mobileBlockId
        : desktopBlockId,
    );
  }, [adLog, desktopBlockId, mobileBlockId]);

  const initAd = useCallback((firstRequest: boolean) => {
    if (
      lastInitTs.current &&
      Date.now() - lastInitTs.current < AD_AUTOREFRESH_INTERVAL_MS
    ) {
      log(`skipping floor_ad refresh since it was recently`);
      return;
    }

    lastInitTs.current = Date.now();
    window.yaContextCb = window.yaContextCb || [];

    window.yaContextCb.push(() => {
      const advManager = getAdvManager();
      const platform = advManager.getPlatform();
      const blockId = platform === 'desktop' ? desktopBlockId : mobileBlockId;

      let unbindClickListener: (() => void) | null = null;
      if (firstRequest) {
        adLog.requested(blockId);
      }

      advManager.render(
        {
          blockId,
          type: 'floorAd',
          platform,
          onRender: (data) => {
            log('rendered', data);
            if (!firstRequest) {
              return;
            }
            adLog.gotResponse(blockId, {
              AdResponse: AdResponseType.HAS_ADS,
            });
            adLog.rendered(blockId);

            if (!renderWasCalledRef.current) {
              // render can be called multiple times, in case user leaves tab and then returns some time later
              renderWasCalledRef.current = true;
              adLog.shown(blockId);

              if (data.stickyAdNode) {
                const element = data.stickyAdNode;

                unbindClickListener = bindClickLogger(element, () => {
                  log('clicked');
                  adLog.clicked(blockId);
                });
              }
            }

            forceFooterBottomPadding(data.floorAdSize?.height);
          },
          onError: (data) => {
            log('error', data);
          },
          onClose: () => {
            log('closed');
            setAdWasClosed(true);
            removeFooterBottomPadding();
            safeSessionStorage.setItem(sessionStorageKey, '1');
            adLog.closed(blockId);

            if (unbindClickListener) {
              unbindClickListener();
            }
          },
        },
        () => {
          log('fallback called');

          if (firstRequest) {
            adLog.gotResponse(blockId, {AdResponse: AdResponseType.NO_ADS});
            adLog.nothingToShow(blockId);
          }
        },
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!canShowAd) {
      return;
    }

    initAd(true);

    return () => {
      removeFooterBottomPadding();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const windowHasFocus = useWindowFocusListener(
    useCallback(() => {
      if (renderWasCalledRef.current && canShowAd) {
        log('refreshing floor_ad on window focus');
        initAd(false);
      }
    }, [initAd, canShowAd]),
  );

  useEffect(() => {
    if (!windowHasFocus || !canShowAd) {
      return;
    }
    const interval = setInterval(() => {
      if (renderWasCalledRef.current) {
        log('refreshing floor_ad on timer');
        initAd(false);
      }
    }, AD_AUTOREFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [windowHasFocus, canShowAd, initAd]);

  if (!canShowAd) {
    return null;
  }

  return <YandexAdScript onError={onScriptError} />;
};
